import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  X,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Loader2,
  Check,
  Coins,
  AlertTriangle,
  Image as ImageIcon,
  Wand2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_REFS = 5;
const MAX_LONG_EDGE = 1600;

// Compress + resize single file to base64 data URL (JPEG 85)
const fileToDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const max = Math.max(width, height);
        if (max > MAX_LONG_EDGE) {
          const scale = MAX_LONG_EDGE / max;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const c = document.createElement('canvas');
        c.width = width;
        c.height = height;
        c.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ------------- Screen 4 & 5: upload + confirm -------------
export const FusionUploadScreen = ({
  carouselId,
  initialNotes = '',
  onCancel,
  onComplete,
}) => {
  const [refs, setRefs] = useState([]); // base64 data URLs
  const [notes, setNotes] = useState(initialNotes);
  const [warning, setWarning] = useState('');
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState('upload'); // 'upload' | 'confirming' | 'fusing'
  const [confirmText, setConfirmText] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [fusionJob, setFusionJob] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/api/carousel/settings`)
      .then((r) => setWarning(r.data.fusion_cost_warning || ''))
      .catch(() => {});
  }, []);

  const addFiles = async (files) => {
    if (!files || files.length === 0) return;
    const remain = MAX_REFS - refs.length;
    if (remain <= 0) {
      toast.warning(`最多 ${MAX_REFS} 張`);
      return;
    }
    setUploading(true);
    try {
      const arr = Array.from(files).slice(0, remain);
      const urls = await Promise.all(arr.map(fileToDataURL));
      setRefs((prev) => [...prev, ...urls]);
    } catch (e) {
      toast.error('圖片處理失敗');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const submitForConfirm = async () => {
    if (!notes.trim()) {
      // skip confirm, go directly to fusion (refs optional)
      return startFusion();
    }
    setConfirmLoading(true);
    setStage('confirming');
    try {
      const r = await axios.post(`${API}/api/carousel/fusion/confirm`, {
        carousel_id: carouselId,
        notes,
        ref_images: refs,
      });
      setConfirmText(r.data.message || '');
    } catch (e) {
      toast.error('確認失敗：' + (e.response?.data?.detail || e.message));
      setStage('upload');
    } finally {
      setConfirmLoading(false);
    }
  };

  const startFusion = async () => {
    setStage('fusing');
    try {
      const r = await axios.post(`${API}/api/carousel/fusion/start`, {
        carousel_id: carouselId,
        notes,
        ref_images: refs,
      });
      const jobId = r.data.job_id;
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const jr = await axios.get(`${API}/api/carousel/fusion/job/${jobId}`);
          const job = jr.data;
          setFusionJob(job);
          if (job.status === 'done') {
            clearInterval(poll);
            toast.success('底圖生成完成');
            onComplete(job);
            // Reset internal state so the tab shows the upload form again next time
            setStage('upload');
            setRefs([]);
            setNotes('');
            setConfirmText('');
            setFusionJob(null);
          } else if (job.status === 'failed') {
            clearInterval(poll);
            toast.error('生成底圖失敗：' + (job.error || '未知'));
            setStage('upload');
          }
        } catch (err) {
          if (err.response?.status === 404) {
            clearInterval(poll);
            toast.error('任務不存在');
            setStage('upload');
          }
        }
        if (attempts > 150) {
          clearInterval(poll);
          toast.error('超時（超過 5 分鐘）');
          setStage('upload');
        }
      }, 3000);
    } catch (e) {
      toast.error('啟動底圖生成失敗：' + (e.response?.data?.detail || e.message));
      setStage('upload');
    }
  };

  if (stage === 'confirming') {
    return (
      <section className="max-w-2xl mx-auto" data-testid="fusion-confirm-screen">
        <div className="flex items-center gap-2 mb-4">
          <Wand2 className="w-4 h-4 text-atelier-coral" />
          <span className="atelier-caption text-atelier-coral">AI · 需求確認</span>
        </div>
        <h2 className="font-display text-2xl md:text-3xl text-atelier-ink tracking-tight mb-6">
          等我確認下方向
        </h2>

        {confirmLoading ? (
          <div className="py-10 text-center text-atelier-mute text-sm">
            <Loader2 className="w-6 h-6 animate-spin text-atelier-coral mx-auto mb-3" />
            AI 正在閱讀你的風格筆記同參考圖...
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-atelier-bg/70 border border-atelier-line rounded-xl p-5 mb-5 text-sm text-atelier-ink leading-relaxed whitespace-pre-wrap"
            data-testid="fusion-confirm-text"
          >
            {confirmText}
          </motion.div>
        )}

        <div className="flex gap-2 justify-end flex-wrap">
          <button
            onClick={() => setStage('upload')}
            disabled={confirmLoading}
            className="atelier-btn-ghost h-9 px-3.5 text-[12.5px] disabled:opacity-40"
            data-testid="fusion-confirm-back"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={1.8} />
            返去改
          </button>
          <button
            onClick={startFusion}
            disabled={confirmLoading || !confirmText}
            className="atelier-btn-primary atelier-glow-coral h-9 px-3.5 text-[12.5px] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="fusion-confirm-go"
          >
            <Check className="w-3 h-3" strokeWidth={2} />
            確認生成
          </button>
        </div>
      </section>
    );
  }

  if (stage === 'fusing') {
    const j = fusionJob || {};
    const stageLabel =
      {
        queued: '排隊中...',
        analyzing_vibe: 'AI 正在分析你的參考圖及 7 頁版面…',
        generating_backgrounds: `AI 圖像引擎正在生成 ${j.total_bg_pages || ''} 張品牌底圖…`,
        done: '完成！',
        failed: '失敗',
      }[j.stage] || '處理中...';

    return (
      <section className="max-w-2xl mx-auto py-20 text-center" data-testid="fusion-progress-screen">
        <Loader2 className="w-10 h-10 animate-spin text-atelier-coral mx-auto mb-5" />
        <h3 className="font-display text-xl text-atelier-ink mb-2">{stageLabel}</h3>
        {j.vibe_brief && (
          <p className="text-sm text-atelier-coral max-w-md mx-auto mt-4 italic">
            風格方向：{j.vibe_brief}
          </p>
        )}
        <p className="text-[11px] text-atelier-subtle mt-4">
          通常需要 1 至 2 分鐘（包括 7 張品牌底圖並行生成）
        </p>
      </section>
    );
  }

  // Stage: upload
  return (
    <section
      className="max-w-2xl mx-auto w-full"
      data-testid="fusion-upload-screen"
    >
      <div className="px-4 pt-4 pb-2">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-px w-8 bg-atelier-lineHi" />
        <Sparkles className="w-3.5 h-3.5 text-atelier-coral" strokeWidth={1.6} />
        <span className="atelier-caption text-atelier-coral">Brand · Fusion</span>
        <span className="w-1.5 h-1.5 rounded-full bg-atelier-coral atelier-breathe" />
      </div>
      <h2
        className="text-xl md:text-2xl text-atelier-ink tracking-[-0.02em] leading-[1.25] mb-4"
        style={{ fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif", fontWeight: 500 }}
      >
        生成<em className="not-italic" style={{ color: 'var(--atelier-coral)', fontStyle: 'italic' }}>專屬品牌</em>底圖
      </h2>
      <p className="text-atelier-mute text-[15px] mb-8" style={{ lineHeight: 1.8 }}>
        上傳 1 至 5 張參考圖（色彩、質感、氛圍）或 <strong className="text-atelier-ink font-medium">留空完全交由 AI 判斷</strong>——
        AI 會根據主題與筆記自動決定風格，並生成品牌底圖融入版面。
      </p>

      {warning && (
        <div
          className="flex gap-2.5 items-start bg-atelier-amber/5 border border-atelier-amber/40 rounded-xl p-3.5 mb-6 text-[12.5px] text-atelier-ink"
          data-testid="fusion-cost-warning"
        >
          <AlertTriangle className="w-4 h-4 text-atelier-amber flex-shrink-0 mt-0.5" strokeWidth={1.6} />
          <span>{warning}</span>
        </div>
      )}

      {/* Upload Zone */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-atelier-line hover:border-atelier-coral bg-atelier-surface hover:bg-atelier-coral/[0.04] rounded-2xl p-8 mb-4 text-center cursor-pointer transition-all duration-300 group"
        data-testid="fusion-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => addFiles(e.target.files)}
        />
        <Upload className="w-7 h-7 text-atelier-subtle group-hover:text-atelier-coral mx-auto mb-3 transition-colors" strokeWidth={1.4} />
        <div className="text-[15px] text-atelier-ink font-medium">
          拖放或點擊上傳參考圖 <span className="text-atelier-subtle font-normal">（選填）</span>
        </div>
        <div className="atelier-caption mt-2">
          {refs.length}/{MAX_REFS} · JPG/PNG · 若沒有上傳，會完全交由 AI 判斷
        </div>
      </div>

      {refs.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-5">
          {refs.map((url, i) => (
            <div
              key={i}
              className="relative aspect-square rounded-xl overflow-hidden border border-atelier-line"
              data-testid={`fusion-ref-${i}`}
            >
              <img src={url} alt={`ref ${i}`} className="w-full h-full object-cover" />
              <button
                onClick={() => setRefs((prev) => prev.filter((_, j) => j !== i))}
                className="absolute top-1.5 right-1.5 bg-atelier-ink/70 hover:bg-red-500 rounded-full p-1 transition-colors"
              >
                <X className="w-3 h-3 text-atelier-ink" strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mb-5">
        <label className="atelier-caption mb-2.5 block">風格需求 <span className="text-atelier-subtle normal-case tracking-normal">（選填）</span></label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="例：我要復古雜誌感、暖色調、有膠片顆粒質感；或者 Y2K 螢光感、濃烈對比"
          className="w-full bg-atelier-surface border border-atelier-line focus:border-atelier-coral/60 rounded-xl px-4 py-3.5 text-atelier-ink text-[15px] outline-none resize-y transition-colors placeholder:text-atelier-subtle"
          data-testid="fusion-notes"
        />
        <p className="atelier-caption mt-2.5">
          若有填寫，AI 會先回覆確認方向，之後再開始生成底圖。
        </p>
      </div>

      </div>
      <div className="flex gap-2 justify-between flex-wrap px-4 py-3 border-t border-atelier-line">
        <button
          onClick={onCancel}
          className="atelier-btn-ghost h-9 px-3.5 text-[12.5px]"
          data-testid="fusion-upload-cancel"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={1.8} />
          返回
        </button>
        <button
          onClick={submitForConfirm}
          disabled={uploading}
          className="atelier-btn-primary atelier-glow-coral h-9 px-3.5 text-[12.5px] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="fusion-upload-next"
        >
          {notes.trim() ? (
            <>
              下一步
              <ArrowRight className="w-3 h-3" strokeWidth={1.8} />
            </>
          ) : (
            <>
              <Wand2 className="w-3 h-3" strokeWidth={1.8} />
              {refs.length === 0 ? '直接生成' : '生成底圖'}
            </>
          )}
        </button>
      </div>
    </section>
  );
};
